// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// material ui
import {
  Alert,
  AlertTitle,
  Box,
  Divider,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import PortfolioAnalysisFilterInput from '@/features/energyxplorer/portfolio/components/meter-drawer/charts/PortfolioAnalysisFilterInput';

// relative
import DurationAnalysisChart from '../../../../components/analysis/DurationAnalysisChart';

interface FilterState {
  activePeriodFilter: boolean | undefined;
  activeDaysFilter: boolean | undefined;
  activeHolidaysFilter: boolean | undefined;
}

interface Props {
  data?: object | null;
  isLoading?: boolean;
  setFilterOptions: (filters: FilterState) => void;
}

const DurationAnalysisDialog = ({
  data = null,
  isLoading = true,
  setFilterOptions,
}: Props) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const handleApplyFilters = async (filters: FilterState) => {
    setFilterOptions(filters);
  };
  const [xAxisMode, setXAxisMode] = useState('percentage');

  return (
    <>
      <Box display={'flex'} flexDirection={'column'}>
        <Box display={'flex'} sx={{ mt: 2 }}>
          <Grid container columns={12} width={'100%'}>
            <Grid
              size={{
                md: 7,
              }}
            >
              {data ? (
                <DurationAnalysisChart
                  data={data}
                  xAxisMode={xAxisMode}
                  isLoading={isLoading}
                />
              ) : (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>{t('common.no_data_available')}</AlertTitle>
                  {t('hour_analysis.missing_children_description')}
                </Alert>
              )}
            </Grid>
            <Grid
              size={{
                md: 5,
              }}
            >
              <Divider orientation="vertical" sx={{ height: 'auto' }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  {t('duration_analysis.filtering')}
                </Typography>
                <InfoTooltipIcon
                  title={t('energyxplorer:tooltips.applyFilteringForInsight')}
                />
              </Box>
              <PortfolioAnalysisFilterInput
                handleApply={handleApplyFilters}
                showDaysFilter={false}
                showPeriodFilter={true}
                showHolidaysFilter={true}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                >
                  {t('common.show')}
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={xAxisMode}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue) setXAxisMode(newValue);
                }}
                sx={{ my: 2 }}
              >
                <Tooltip
                  title={t('energyxplorer:tooltips.showInHours')}
                  placement="top"
                >
                  <ToggleButton sx={{ borderRadius: 2 }} value="duration">
                    <AccessTimeRoundedIcon />
                  </ToggleButton>
                </Tooltip>
                <Tooltip
                  title={t('energyxplorer:tooltips.showInPercent')}
                  placement="top"
                >
                  <ToggleButton sx={{ borderRadius: 2 }} value="percentage">
                    <PercentRoundedIcon />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default DurationAnalysisDialog;
